import React, { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import MessageDetailModal from '../../components/admin/MessageDetailModal';
import axios from 'axios';
import { buildApiUrl } from '../../config/api';

const AdminMessages = () => {
    const [messages, setMessages] = useState([]);
    const [filteredMessages, setFilteredMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        unread: 0,
        read: 0,
        replied: 0
    });
    const [filters, setFilters] = useState({
        status: 'all',
        search: '',
        from_date: '',
        to_date: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [updating, setUpdating] = useState(false);

    const getHeaders = useCallback(() => {
        const token = localStorage.getItem('token');
        return {
            Authorization: `Bearer ${token}`,
            'x-auth-token': token
        };
    }, []);

    const fetchStats = useCallback(async (sourceMessages = []) => {
        try {
            const response = await axios.get(buildApiUrl('/contact/messages/stats'), {
                headers: getHeaders()
            });

            const statsData = response.data?.data;
            if (statsData) {
                setStats(statsData);
                return;
            }

            const messagesList = Array.isArray(sourceMessages) ? sourceMessages : [];
            setStats({
                total: messagesList.length,
                unread: messagesList.filter((m) => ['new', 'unread', null, undefined].includes(m.status)).length,
                read: messagesList.filter((m) => m.status === 'read').length,
                replied: messagesList.filter((m) => m.status === 'replied').length
            });
        } catch (error) {
            console.error('Error fetching stats:', error);
            const messagesList = Array.isArray(sourceMessages) ? sourceMessages : [];
            setStats({
                total: messagesList.length,
                unread: messagesList.filter((m) => ['new', 'unread', null, undefined].includes(m.status)).length,
                read: messagesList.filter((m) => m.status === 'read').length,
                replied: messagesList.filter((m) => m.status === 'replied').length
            });
        }
    }, [getHeaders]);

    const fetchMessages = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(buildApiUrl('/contact/messages'), {
                headers: getHeaders()
            });
            const messagesData = response.data?.data || [];
            setMessages(messagesData);
            setFilteredMessages(messagesData);
            await fetchStats(messagesData);
        } catch (error) {
            console.error('Error fetching messages:', error);
            alert(error.response?.data?.message || 'Failed to load messages');
        } finally {
            setLoading(false);
        }
    }, [fetchStats, getHeaders]);

    const filterMessages = useCallback(() => {
        let filtered = [...messages];

        // Status filter
        if (filters.status !== 'all') {
            filtered = filtered.filter(m => m.status === filters.status);
        }

        // Search filter
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(m => 
                m.name?.toLowerCase().includes(searchLower) ||
                m.email?.toLowerCase().includes(searchLower) ||
                m.subject?.toLowerCase().includes(searchLower) ||
                m.message?.toLowerCase().includes(searchLower)
            );
        }

        // Date filters
        if (filters.from_date) {
            filtered = filtered.filter(m => 
                new Date(m.created_at) >= new Date(filters.from_date)
            );
        }
        if (filters.to_date) {
            filtered = filtered.filter(m => 
                new Date(m.created_at) <= new Date(filters.to_date)
            );
        }

        setFilteredMessages(filtered);
    }, [filters, messages]);

    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    useEffect(() => {
        filterMessages();
    }, [filterMessages]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({
            status: 'all',
            search: '',
            from_date: '',
            to_date: ''
        });
        setFilteredMessages(messages);
    };

    const handleStatusUpdate = useCallback(async (id, status) => {
        try {
            setUpdating(true);
            await axios.patch(
                buildApiUrl(`/contact/messages/${id}/status`),
                { status },
                { headers: getHeaders() }
            );
            
            // Update local state
            setMessages(prev => 
                prev.map(m => m.id === id ? { ...m, status } : m)
            );
            
            // Update stats
            const nextMessages = messages.map(m => m.id === id ? { ...m, status } : m);
            fetchStats(nextMessages);
            
            // Show success message
            alert('Message status updated successfully!');
        } catch (error) {
            console.error('Error updating message:', error);
            alert(error.response?.data?.message || 'Failed to update message status');
        } finally {
            setUpdating(false);
        }
    }, [fetchStats, getHeaders, messages]);

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
            return;
        }

        try {
            await axios.delete(
                buildApiUrl(`/contact/messages/${id}`),
                { headers: getHeaders() }
            );
            
            // Remove from local state
            const nextMessages = messages.filter(m => m.id !== id);
            setMessages(nextMessages);
            
            // Update stats
            fetchStats(nextMessages);
            
            alert('Message deleted successfully!');
        } catch (error) {
            console.error('Error deleting message:', error);
            alert(error.response?.data?.message || 'Failed to delete message');
        }
    }, [fetchStats, getHeaders, messages]);

    const handleMarkAsRead = (id) => {
        handleStatusUpdate(id, 'read');
    };

    const handleMarkAsReplied = (id) => {
        handleStatusUpdate(id, 'replied');
    };

    const openMessage = (message) => {
        setSelectedMessage(message);
        setShowModal(true);
        
        // Auto-mark as read if unread
        if (message.status === 'unread' || message.status === 'new' || !message.status) {
            handleStatusUpdate(message.id, 'read');
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) {
            return 'Today';
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays < 7) {
            return `${diffDays} days ago`;
        } else {
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        }
    };

    const getStatusBadgeClass = (status) => {
        switch(status) {
            case 'new':
            case 'unread': return 'status-badge danger';
            case 'read': return 'status-badge info';
            case 'replied': return 'status-badge success';
            default: return 'status-badge secondary';
        }
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'new':
            case 'unread': return 'fas fa-envelope';
            case 'read': return 'fas fa-envelope-open';
            case 'replied': return 'fas fa-reply';
            default: return 'fas fa-envelope';
        }
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader 
                    title="Message Management" 
                    subtitle="View and manage customer inquiries"
                />

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #334f7f 0%, #764ba2 100%)' }}>
                            <i className="fas fa-envelope"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-label">Total Messages</span>
                            <span className="stat-value">{stats.total}</span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f56565 0%, #e53e3e 100%)' }}>
                            <i className="fas fa-envelope"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-label">Unread</span>
                            <span className="stat-value">{stats.unread}</span>
                            {stats.unread > 0 && (
                                <span className="stat-trend warning">
                                    {stats.unread} messages pending
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)' }}>
                            <i className="fas fa-envelope-open"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-label">Read</span>
                            <span className="stat-value">{stats.read}</span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)' }}>
                            <i className="fas fa-reply"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-label">Replied</span>
                            <span className="stat-value">{stats.replied}</span>
                        </div>
                    </div>
                </div>

                {/* Filters Section */}
                <div className="filters-card">
                    <div className="filters-header">
                        <h3>
                            <i className="fas fa-filter"></i>
                            Filter Messages
                        </h3>
                        <button 
                            className="toggle-filters"
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            {showFilters ? (
                                <>
                                    <i className="fas fa-chevron-up"></i>
                                    Hide Filters
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-chevron-down"></i>
                                    Show Filters
                                </>
                            )}
                        </button>
                    </div>

                    {showFilters && (
                        <div className="filters-body">
                            <div className="filters-grid">
                                <div className="filter-group">
                                    <label>
                                        <i className="fas fa-search"></i>
                                        Search
                                    </label>
                                    <input
                                        type="text"
                                        name="search"
                                        placeholder="Name, email, subject..."
                                        value={filters.search}
                                        onChange={handleFilterChange}
                                        className="filter-input"
                                    />
                                </div>

                                <div className="filter-group">
                                    <label>
                                        <i className="fas fa-tag"></i>
                                        Status
                                    </label>
                                    <select
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                        className="filter-select"
                                    >
                                        <option value="all">All Messages</option>
                                        <option value="unread">Unread</option>
                                        <option value="read">Read</option>
                                        <option value="replied">Replied</option>
                                    </select>
                                </div>

                                <div className="filter-group">
                                    <label>
                                        <i className="fas fa-calendar-alt"></i>
                                        From Date
                                    </label>
                                    <input
                                        type="date"
                                        name="from_date"
                                        value={filters.from_date}
                                        onChange={handleFilterChange}
                                        className="filter-input"
                                    />
                                </div>

                                <div className="filter-group">
                                    <label>
                                        <i className="fas fa-calendar-alt"></i>
                                        To Date
                                    </label>
                                    <input
                                        type="date"
                                        name="to_date"
                                        value={filters.to_date}
                                        onChange={handleFilterChange}
                                        className="filter-input"
                                    />
                                </div>

                                <div className="filter-actions">
                                    <button className="reset-btn" onClick={clearFilters}>
                                        <i className="fas fa-undo-alt"></i>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Results Summary */}
                <div className="results-summary">
                    <div className="results-info">
                        <i className="fas fa-envelope"></i>
                        <span>
                            Showing <strong>{filteredMessages.length}</strong> of <strong>{messages.length}</strong> messages
                        </span>
                    </div>
                    <div className="results-actions">
                        <button className="refresh-btn" onClick={() => {
                            fetchMessages();
                            fetchStats();
                        }}>
                            <i className="fas fa-sync-alt"></i>
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Messages List */}
                <div className="messages-container">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading messages...</p>
                        </div>
                    ) : (
                        <div className="messages-list">
                            {filteredMessages.length > 0 ? (
                                filteredMessages.map(message => (
                                    <div 
                                        key={message.id} 
                                        className={`message-card ${message.status === 'unread' ? 'unread' : ''}`}
                                        onClick={() => openMessage(message)}
                                    >
                                        <div className="message-avatar">
                                            {message.name?.charAt(0).toUpperCase()}
                                        </div>
                                        
                                        <div className="message-content">
                                            <div className="message-header">
                                                <div className="sender-info">
                                                    <h4 className="sender-name">{message.name}</h4>
                                                    <span className="sender-email">{message.email}</span>
                                                    {message.phone && (
                                                        <span className="sender-phone">
                                                            <i className="fas fa-phone"></i>
                                                            {message.phone}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="message-meta">
                                                    <span className="message-time">
                                                        <i className="far fa-clock"></i>
                                                        {formatDate(message.created_at)}
                                                    </span>
                                                    <span className={getStatusBadgeClass(message.status)}>
                                                        <i className={getStatusIcon(message.status)}></i>
                                                        {message.status}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="message-subject">
                                                <strong>{message.subject || 'No Subject'}</strong>
                                            </div>

                                            <div className="message-preview">
                                                {message.message?.substring(0, 150)}...
                                            </div>

                                            <div className="message-footer">
                                                <div className="message-actions">
                                                    {message.status === 'unread' && (
                                                        <button 
                                                            className="action-btn small primary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMarkAsRead(message.id);
                                                            }}
                                                        >
                                                            <i className="fas fa-check"></i>
                                                            Mark as Read
                                                        </button>
                                                    )}
                                                    {message.status !== 'replied' && (
                                                        <button 
                                                            className="action-btn small success"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMarkAsReplied(message.id);
                                                            }}
                                                        >
                                                            <i className="fas fa-reply"></i>
                                                            Mark as Replied
                                                        </button>
                                                    )}
                                                    <button 
                                                        className="action-btn small danger"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(message.id);
                                                        }}
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state">
                                    <i className="fas fa-inbox"></i>
                                    <h3>No Messages Found</h3>
                                    <p>No messages match your current filters.</p>
                                    <button className="clear-filters-btn" onClick={clearFilters}>
                                        Clear Filters
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Message Detail Modal */}
            {showModal && selectedMessage && (
                <MessageDetailModal
                    message={selectedMessage}
                    onClose={() => setShowModal(false)}
                    onMarkAsRead={handleMarkAsRead}
                    onMarkAsReplied={handleMarkAsReplied}
                    onDelete={handleDelete}
                    updating={updating}
                />
            )}
        </div>
    );
};

export default AdminMessages;
